import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2, Plus } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'
import {
  getMyHome, createMyHome, type MyDevice,
  getMyHomeOverview, getMyHomePlan, getMyHomeForecast, getMyHomeEvents, addMyHomeEvent, stopMyHome,
  addMyDevice,
} from '../api/energy-os'
import { energyOsClient } from '../api/client'
import { EnergyOSDeviceGrid } from '../components/EnergyOSDeviceGrid'
import { EnergyOSPlanTimeline } from '../components/EnergyOSPlanTimeline'
import { ChartView } from '../components/PlateViewRenderer'
import { Modal } from '../components/Modal'
import { Field } from '../components/Field'
import { ENERGY_OS_SIGNUP_HOME_KEY } from './EnergyOSSignup'
import type { BPlateChartType } from '../types'

// One device type list, one form — no more "simulated vs real appliance" split. The vendor and
// connectionMode a device gets are inferred from its type: solar/battery/meter are the
// simulated/controllable fleet (vendor:'mock'), appliance is a real, ingest-only Shelly plug.
const DEVICE_TYPES = ['solar', 'battery', 'meter', 'appliance'] as const

export function EnergyOSHomeownerDashboard() {
  const queryClient = useQueryClient()

  const { data: home, isLoading, error, refetch } = useQuery({
    queryKey: ['my-home'],
    queryFn: () => getMyHome().then((r) => r.data),
    retry: false,
  })

  const noHomeYet = (error as any)?.response?.status === 404

  return (
    <div className="space-y-6">
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={20} className="animate-spin text-ink-dim" />
        </div>
      ) : noHomeYet ? (
        <HomeSetup onCreated={() => refetch()} />
      ) : home ? (
        <HomeDashboardContent
          homeCoffeeId={home.homeCoffeeId}
          devices={home.devices}
          onDeviceAdded={() => queryClient.invalidateQueries({ queryKey: ['my-home'] })}
        />
      ) : (
        <p className="text-sm text-red-500">Failed to load your home. Please refresh.</p>
      )}
    </div>
  )
}

// Runs once after first login: if the signup form stashed a country/MPID, create the home
// automatically instead of asking the user to type it in twice. Falls back to a plain form —
// covering both "auto-create failed (e.g. MPID already taken)" and "verified on a different
// device, so nothing was stashed here at all."
function HomeSetup({ onCreated }: { onCreated: () => void }) {
  const [country, setCountry] = useState('')
  const [mpid, setMpid] = useState('')
  const [error, setError] = useState('')
  const [autoCreating, setAutoCreating] = useState(false)
  const [pending, setPending] = useState(false)
  const [attemptedAuto, setAttemptedAuto] = useState(false)

  useEffect(() => {
    if (attemptedAuto) return
    setAttemptedAuto(true)
    const stashed = localStorage.getItem(ENERGY_OS_SIGNUP_HOME_KEY)
    if (!stashed) return
    try {
      const { country: c, mpid: m } = JSON.parse(stashed)
      if (!c || !m) return
      setCountry(c); setMpid(m)
      setAutoCreating(true)
      createMyHome({ mpid: m, country: c })
        .then(() => { localStorage.removeItem(ENERGY_OS_SIGNUP_HOME_KEY); onCreated() })
        .catch((err) => {
          setError(err.response?.data?.error || 'Could not finish setting up your home automatically — please confirm the details below.')
          setAutoCreating(false)
        })
    } catch {
      // malformed stash — ignore, fall through to the manual form
    }
  }, [attemptedAuto, onCreated])

  const handleSubmit = async () => {
    if (!country || !mpid) { setError('Country and MPID are required'); return }
    setError(''); setPending(true)
    try {
      await createMyHome({ mpid, country })
      localStorage.removeItem(ENERGY_OS_SIGNUP_HOME_KEY)
      onCreated()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set up your home')
    } finally {
      setPending(false)
    }
  }

  if (autoCreating) {
    return (
      <div className="card p-8 flex flex-col items-center justify-center text-center gap-3">
        <Loader2 size={20} className="animate-spin text-accent" />
        <p className="text-sm text-ink-dim">Setting up your home…</p>
      </div>
    )
  }

  return (
    <div className="card p-6">
      <h2 className="text-sm font-semibold text-ink mb-1">Finish setting up your home</h2>
      <p className="text-xs text-ink-dim mb-4">
        Enter your meter's details to connect it to EnergyOS.
      </p>
      {error && <p className="text-xs text-red-600 mb-3">{error}</p>}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Country">
          <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="FR" className="field" />
        </Field>
        <Field label="MPID">
          <input value={mpid} onChange={(e) => setMpid(e.target.value)} placeholder="123456789012" className="field" />
        </Field>
      </div>
      <Button variant="primary" onClick={handleSubmit} loading={pending}>
        {pending ? 'Saving…' : 'Connect my home'}
      </Button>
    </div>
  )
}

// Brings the logged-in homeowner dashboard to parity with the OpenPlace token dashboard
// (EnergyOSHomeDashboard.tsx): Overview stats, Emergency Stop, Today's Plan, Forecast, Events —
// all fetched through the JWT-scoped "me" API instead of a token-scoped Drinker/Preparation run.
function HomeDashboardContent({ homeCoffeeId, devices, onDeviceAdded }: {
  homeCoffeeId: string
  devices: MyDevice[]
  onDeviceAdded: () => void
}) {
  const queryClient = useQueryClient()
  const [stopping, setStopping] = useState(false)
  const [stopped, setStopped] = useState(false)

  const { data: overview } = useQuery({
    queryKey: ['my-home-overview', homeCoffeeId],
    queryFn: () => getMyHomeOverview().then((r) => r.data),
    refetchInterval: 30_000,
  })
  const { data: plan } = useQuery({
    queryKey: ['my-home-plan', homeCoffeeId],
    queryFn: () => getMyHomePlan().then((r) => r.data),
    refetchInterval: 30_000,
  })
  const { data: forecast } = useQuery({
    queryKey: ['my-home-forecast', homeCoffeeId],
    queryFn: () => getMyHomeForecast().then((r) => r.data),
  })

  const handleStop = async () => {
    if (!window.confirm("Stop all automation for this home for the next hour? Today's plan will be marked stopped.")) return
    setStopping(true)
    try {
      await stopMyHome()
      setStopped(true)
      queryClient.invalidateQueries({ queryKey: ['my-home-plan', homeCoffeeId] })
    } catch {
      // best-effort — button state below just reflects whether the call completed
    } finally {
      setStopping(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-sm font-semibold text-ink">Your home</h1>
        <Button variant="danger" icon={AlertTriangle} loading={stopping} onClick={handleStop} disabled={stopping}>
          {stopped ? 'Stopped' : 'Emergency Stop'}
        </Button>
      </div>

      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <OverviewStat label="MPID" value={overview.mpid ?? '—'} to="/energy-os/dashboard/home" />
          <OverviewStat label="Solar" value={overview.solarPowerKw !== undefined ? `${overview.solarPowerKw} kW` : '—'} />
          <BatteryStat percent={overview.batterySocPercent} />
          <OverviewStat
            label="Grid"
            value={overview.meterPowerKw !== undefined ? `${overview.meterPowerKw} kW` : '—'}
          />
        </div>
      )}

      <DevicesSection devices={devices} onDeviceAdded={onDeviceAdded} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <section className="card p-5">
          <h2 className="text-sm font-semibold text-ink mb-1">Today's Plan</h2>
          <EnergyOSPlanTimeline plan={plan?.plan ?? undefined} actions={plan?.actions ?? []} />
        </section>

        <ForecastCard data={forecast} />
      </div>

      <EventsSection homeCoffeeId={homeCoffeeId} />
    </div>
  )
}

function OverviewStat({ label, value, to }: { label: string; value: string | number; to?: string }) {
  const content = (
    <>
      <p className="text-[10px] font-semibold text-ink-dim uppercase tracking-wider mb-1 truncate">{label}</p>
      <p className="text-xl font-semibold text-ink truncate">{value}</p>
    </>
  )
  if (to) {
    return <Link to={to} className="card p-4 block hover:bg-paper-sunken transition-colors">{content}</Link>
  }
  return <div className="card p-4">{content}</div>
}

// Red near-empty, orange around half, green near-full — interpolated continuously (not three
// hard bands) so the color shifts smoothly as the battery charges/discharges instead of jumping.
function batteryColor(percent: number): string {
  const stops: { at: number; hex: string }[] = [
    { at: 0, hex: '#f04438' }, { at: 50, hex: '#f79009' }, { at: 100, hex: '#12b76a' },
  ]
  const p = Math.max(0, Math.min(100, percent))
  const hexToRgb = (hex: string) => {
    const n = parseInt(hex.slice(1), 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  }
  let lo = stops[0], hi = stops[1]
  for (let i = 0; i < stops.length - 1; i++) {
    if (p >= stops[i].at && p <= stops[i + 1].at) { lo = stops[i]; hi = stops[i + 1]; break }
  }
  const t = (p - lo.at) / (hi.at - lo.at)
  const [r1, g1, b1] = hexToRgb(lo.hex)
  const [r2, g2, b2] = hexToRgb(hi.hex)
  const mix = (a: number, b: number) => Math.round(a + (b - a) * t)
  return `rgb(${mix(r1, r2)}, ${mix(g1, g2)}, ${mix(b1, b2)})`
}

// Battery-shaped fill gauge (body + terminal nub) instead of a bare percentage — the fill level
// and its color both track state of charge, so "how much power is in this battery" reads at a
// glance rather than requiring the viewer to parse a number.
function BatteryStat({ percent }: { percent: number | undefined }) {
  return (
    <div className="card p-4">
      <p className="text-[10px] font-semibold text-ink-dim uppercase tracking-wider mb-1 truncate">Battery</p>
      {percent === undefined ? (
        <p className="text-xl font-semibold text-ink">—</p>
      ) : (
        <div className="flex items-center gap-2 mt-1">
          <div className="flex items-center shrink-0">
            <div className="relative h-5 w-9 rounded-[3px] border-2 border-paper-border p-0.5">
              <div
                className="h-full rounded-[1px] transition-all duration-500"
                style={{ width: `${Math.max(6, Math.min(100, percent))}%`, backgroundColor: batteryColor(percent) }}
              />
            </div>
            <div className="h-2 w-1 rounded-r-sm bg-paper-border" />
          </div>
          <p className="text-xl font-semibold text-ink">{Math.round(percent)}%</p>
        </div>
      )}
    </div>
  )
}

function ForecastCard({ data }: { data: Record<string, number> | undefined }) {
  const [chartType, setChartType] = useState<BPlateChartType>('line')
  return (
    <section className="card p-5">
      <h2 className="text-sm font-semibold text-ink mb-1">Forecast</h2>
      <ChartView data={data ?? {}} chartType={chartType} onChartTypeChange={setChartType} />
    </section>
  )
}

// One merged section for every device type — simulated/controllable (solar/battery/meter) and
// real/ingest-only (Shelly plugs, ...) alike, all added through the same form and shown in the
// same glass card grid (Phase 5d-2).
function DevicesSection({ devices, onDeviceAdded }: { devices: MyDevice[]; onDeviceAdded: () => void }) {
  const [showAdd, setShowAdd] = useState(false)

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-ink">Devices</h2>
        <Button variant="secondary" size="sm" icon={Plus} onClick={() => setShowAdd(true)}>
          Add device
        </Button>
      </div>

      <EnergyOSDeviceGrid devices={devices} />

      {showAdd && (
        <AddDeviceModal
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); onDeviceAdded() }}
        />
      )}
    </section>
  )
}

// http(s)://host/... -> ws(s)://host/... — the WS ingest server lives on the same energy-os
// host/port as the REST API, just a different scheme and path.
function toWebSocketUrl(httpUrl: string): string {
  return httpUrl.replace(/^http/, 'ws')
}

// Single unified form for every device type — Type, Sub type, Name, Allow controls. No more
// "simulated device vs real appliance" branch: the vendor/connectionMode a device gets is
// inferred from its type (solar/battery/meter -> simulated/mock, appliance -> real Shelly plug),
// so the homeowner only ever answers the same four questions.
function AddDeviceModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [deviceType, setDeviceType] = useState<string>(DEVICE_TYPES[0])
  const [subType, setSubType] = useState('')
  const [label, setLabel] = useState('')
  const [allowControls, setAllowControls] = useState(false)
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)
  const [wsUrl, setWsUrl] = useState<string | null>(null)

  const isAppliance = deviceType === 'appliance'

  const handleSubmit = async () => {
    if (!label.trim()) { setError('Give this device a name'); return }
    setError(''); setPending(true)
    try {
      const { data } = await addMyDevice({
        deviceType,
        subType: subType.trim() || undefined,
        label: label.trim(),
        vendor: isAppliance ? 'shelly' : 'mock',
        connectionMode: isAppliance ? 'websocket' : 'control',
        controllable: allowControls,
      })
      if (data.ingestToken) {
        // Same host/port as energyOsClient's own baseURL, just ws(s):// instead of http(s):// and
        // a different path — the WS server is attached directly to energy-os's own HTTP server.
        const base = toWebSocketUrl(energyOsClient.defaults.baseURL ?? '')
        setWsUrl(`${base}/ws/equipment/${data.deviceCoffeeId}?token=${data.ingestToken}`)
      } else {
        onAdded()
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add device')
    } finally {
      setPending(false)
    }
  }

  if (wsUrl) {
    return (
      <Modal title="Device added" onClose={onAdded}>
        <div className="space-y-3">
          <p className="text-xs text-ink-dim">
            In the Shelly app, go to this plug's <strong>Settings → Network → Outbound websocket</strong>,
            enable it, and paste this URL as the server address. The plug will then stream its live
            power readings continuously — unlike a plain webhook, this also works for appliances
            that stay powered on all the time (like a fridge), not just ones you turn on and off.
          </p>
          <div className="p-2 bg-paper-sunken border border-paper-border rounded text-theme-xs font-mono break-all select-all">
            {wsUrl}
          </div>
          <Button variant="primary" className="w-full" onClick={onAdded}>Done</Button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title="Add device" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Type">
          <Select value={deviceType} onValueChange={setDeviceType}>
            {DEVICE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </Select>
        </Field>
        <Field label="Sub type" description="e.g. Fridge, Heater, Air conditioner, Computer, Lamp…">
          <input value={subType} onChange={(e) => setSubType(e.target.value)} placeholder="Fridge" className="field" />
        </Field>
        <Field label="Name">
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Kitchen fridge" className="field" />
        </Field>
        <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
          <input type="checkbox" checked={allowControls} onChange={(e) => setAllowControls(e.target.checked)} className="rounded border-paper-border" />
          Allow controls
        </label>
        {isAppliance && (
          <p className="text-xs text-ink-dim">
            Supports Shelly smart plugs today — one plug per appliance (fridge, oven, ...). After adding, you'll get a URL to paste into the Shelly app.
          </p>
        )}
        {error && <p className="text-xs text-red-500">{error}</p>}
        <Button variant="primary" className="w-full" onClick={handleSubmit} loading={pending}>
          {pending ? 'Adding…' : 'Add device'}
        </Button>
      </div>
    </Modal>
  )
}

function EventsSection({ homeCoffeeId }: { homeCoffeeId: string }) {
  const queryClient = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)

  const { data: events } = useQuery({
    queryKey: ['my-home-events', homeCoffeeId],
    queryFn: () => getMyHomeEvents().then((r) => r.data),
  })

  return (
    <section className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-ink">Events</h2>
        <Button variant="secondary" size="sm" icon={Plus} onClick={() => setShowAdd(true)}>Add Event</Button>
      </div>

      {!events || events.length === 0 ? (
        <p className="text-xs text-ink-dim text-center py-6">No events yet</p>
      ) : (
        <div className="divide-y divide-paper-border">
          {events.map((e) => (
            <div key={e.id} className="py-2.5 flex items-center justify-between text-sm gap-3">
              <div className="min-w-0">
                <p className="font-medium text-ink truncate">{e.eventType}</p>
                {e.note && <p className="text-theme-xs text-ink-dim truncate">{e.note}</p>}
              </div>
              <div className="text-theme-xs text-ink-dim text-right shrink-0">
                <p>{e.startTime ?? ''}</p>
                <p>{e.endTime ?? ''}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <MyAddEventModal
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); queryClient.invalidateQueries({ queryKey: ['my-home-events', homeCoffeeId] }) }}
        />
      )}
    </section>
  )
}

function MyAddEventModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [values, setValues] = useState({ eventType: '', startTime: '', endTime: '', note: '' })
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)

  const handleSubmit = async () => {
    if (!values.eventType) { setError('Event type is required'); return }
    setError(''); setPending(true)
    try {
      await addMyHomeEvent(values)
      onAdded()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add event')
    } finally {
      setPending(false)
    }
  }

  return (
    <Modal title="Add Event" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Event type">
          <input value={values.eventType} onChange={(e) => setValues((v) => ({ ...v, eventType: e.target.value }))}
            placeholder="vacation, occupancy, ceremony, ..." className="field" />
        </Field>
        <Field label="Start">
          <input type="datetime-local" value={values.startTime} onChange={(e) => setValues((v) => ({ ...v, startTime: e.target.value }))} className="field" />
        </Field>
        <Field label="End">
          <input type="datetime-local" value={values.endTime} onChange={(e) => setValues((v) => ({ ...v, endTime: e.target.value }))} className="field" />
        </Field>
        <Field label="Note">
          <input value={values.note} onChange={(e) => setValues((v) => ({ ...v, note: e.target.value }))} placeholder="Optional details" className="field" />
        </Field>
        {error && <p className="text-xs text-red-500">{error}</p>}
        <Button onClick={handleSubmit} disabled={pending} className="w-full">
          {pending ? 'Saving…' : 'Add Event'}
        </Button>
      </div>
    </Modal>
  )
}
